// A host that opens its modal in the browser's top layer (Angular CDK 21+ sets
// `popover="manual"` by default; native `<dialog>.showModal()` does the same) paints above every
// normal-flow element, and z-index is not consulted at all. An overlay portaled to document.body
// therefore renders UNDER that modal however high it bids, so it has to join the top layer.

import { type RefObject, useLayoutEffect } from 'react';
import { ensureTopLayerStyles } from './portalOverlay';

const supportsPopover = (el: HTMLElement): boolean => {
    // The same probe CDK uses: without popover, CDK builds no top layer either. A host using
    // `<dialog>.showModal()` still outranks us on an engine too old for popover.
    if (!('showPopover' in el)) {
        return false;
    }
    // A JS-only popover polyfill (e.g. `@oddbird/popover-polyfill`) can define `showPopover` on
    // the prototype without the CSS engine ever matching `:popover-open`, which would promote
    // into the raw UA box with no reset able to reach it. `CSS` itself is undefined in jsdom.
    return typeof CSS !== 'undefined' && CSS.supports('selector(:popover-open)');
};

export const raiseToTopLayer = (el: HTMLElement): void => {
    if (!supportsPopover(el)) {
        return;
    }
    // Never promote unstyled: the UA box would stretch a panel to the viewport edge, which is
    // worse than losing to the modal. Covers a host CSP blocking the sheet as well.
    if (!ensureTopLayerStyles()) {
        return;
    }
    try {
        // setAttribute, not the `popover` IDL property: the property is an expando wherever the
        // attribute is not reflected, which strands the element without the UA popover behaviour.
        el.setAttribute('popover', 'manual');
        // Entry order decides which top-layer element wins, so re-enter to land on top.
        if (el.matches(':popover-open')) {
            el.hidePopover();
        }
        el.showPopover();
    } catch {
        // `[popover]:not(:popover-open)` is `display: none` in the UA sheet, so a half-applied
        // popover erases the overlay outright. Falling back to the z-index path is the lesser bug.
        el.removeAttribute('popover');
    }
};

// Dropping the attribute rather than calling hidePopover leaves the top layer AND takes the UA
// `display: none` with it, so an overlay that stays mounted while hidden keeps its transition.
export const leaveTopLayer = (el: HTMLElement): void => {
    el.removeAttribute('popover');
};

/**
 * Promotes a portaled overlay into the top layer while `active`. Pass the visibility state, not
 * `true`, for an overlay that stays mounted while hidden, so each show re-enters above its peers.
 */
export const useTopLayer = (ref: RefObject<HTMLElement | null>, active: boolean): void => {
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) {
            return;
        }
        if (active) {
            raiseToTopLayer(el);
            return;
        }
        // Dropping the attribute rather than calling hidePopover leaves the top layer AND takes the
        // UA `display: none` with it, so an overlay that stays mounted while hidden keeps its exit
        // transition and costs a style recalc no more than it did before promotion. Left open, each
        // one measurably slows a full recalc — 500 of them tripled it in Chromium and WebKit.
        el.removeAttribute('popover');
    }, [ref, active]);
};
